import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";

import { getUsedSubscriptionPercentage } from "../../../helpers";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { SettingsCard } from "./shared";

/**
 * Billing tab (replaces myAccount/billing/MyBilling). Read-only, same requests as before:
 * - POST payments/getStripeCustomerSubscriptions → your FitClub plan(s)
 * - POST payments/getStripeCustomerInvoices { businessType } → invoices (all paid / open ones, newest first)
 * - POST payments/getStripeCustomerPayments → payments the club made to FitClub (paid, refunded, failed), with receipt
 * (The old page also loaded users/getSubscriptionPlans only for a hidden, switched-off card; not needed.)
 */

const INTERVAL = { month: "Monthly", year: "Yearly" };
const STATUS = {
  active: ["Active", "bg-[#E8F7EE] text-[#1F6B3A]"],
  trialing: ["Free trial", "bg-[#F3E9FF] text-[#5a00a0]"],
  past_due: ["Payment due", "bg-[#FFF4E5] text-[#7A4A00]"],
  unpaid: ["Unpaid", "bg-[#FFE8EB] text-[#B4232F]"],
  incomplete: ["Incomplete", "bg-[#FFF4E5] text-[#7A4A00]"],
  canceled: ["Cancelled", "bg-[#F1EEF2] text-[#595159]"],
  cancelled: ["Cancelled", "bg-[#F1EEF2] text-[#595159]"],
};
// Stripe objects differ per API version (account default 2020-08-27 vs newer); read both shapes.
const subItem = (s) => s?.items?.data?.[0] || {};
const subInterval = (s) => s?.plan?.interval || subItem(s)?.price?.recurring?.interval || "";
const subAmount = (s) => (s?.plan?.amount ?? subItem(s)?.price?.unit_amount ?? 0);
const subStart = (s) => s?.current_period_start || subItem(s)?.current_period_start;
const subEnd = (s) => s?.current_period_end || subItem(s)?.current_period_end;
const lineInterval = (line) => line?.plan?.interval || line?.price?.recurring?.interval || "";
const euro = (cents) => `€${(Number(cents || 0) / 100).toFixed(2).replace(".", ",")}`;
const PAGE = 12;
const PILL = {
  green: "bg-[#E8F7EE] text-[#1F6B3A]",
  amber: "bg-[#FFF4E5] text-[#7A4A00]",
  red: "bg-[#FFE8EB] text-[#B4232F]",
  gray: "bg-[#F1EEF2] text-[#595159]",
  blue: "bg-[#E8F0FF] text-[#1F4FB4]",
};
const PAYMENT_STATUS = {
  paid: { label: "Paid", tone: "green" },
  refunded: { label: "Refunded", tone: "gray" },
  partially_refunded: { label: "Refunded", tone: "amber" },
  failed: { label: "Failed", tone: "red" },
  pending: { label: "Processing", tone: "blue" },
};
const Pill = ({ tone = "gray", children }) => <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-extrabold ${PILL[tone]}`}>{children}</span>;
const LINK = "inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]";
const DOWNLOAD = (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
    <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
  </svg>
);
const ShowAll = ({ count, what, onClick }) => (
  <div className="mt-2 flex justify-center">
    <button type="button" onClick={onClick} className="rounded-xl px-4 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
      Show all {count} {what}
    </button>
  </div>
);

export default function BillingTab() {
  const userReduxState = useSelector(userState);
  const [subs, setSubs] = React.useState(null);
  const [invoices, setInvoices] = React.useState(null);
  const [payments, setPayments] = React.useState(null);
  const [allInvoices, setAllInvoices] = React.useState(false);
  const [allPayments, setAllPayments] = React.useState(false);

  React.useEffect(() => {
    if (!userReduxState.businessType) return;
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}payments/getStripeCustomerSubscriptions`, data: {} });
        if (alive) setSubs(res?.data?.statusCode === 200 && Array.isArray(res.data.data?.subscriptionList) ? res.data.data.subscriptionList : []);
      } catch (e) {
        if (alive) setSubs([]);
      }
      try {
        const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}payments/getStripeCustomerInvoices`, data: { businessType: userReduxState.businessType } });
        if (alive) setInvoices(res?.data?.statusCode === 200 && Array.isArray(res.data.data?.invoicesList) ? res.data.data.invoicesList : []);
      } catch (e) {
        if (alive) setInvoices([]);
      }
      try {
        const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}payments/getStripeCustomerPayments`, data: {} });
        if (alive) setPayments(res?.data?.statusCode === 200 && Array.isArray(res.data.data?.payments) ? res.data.data.payments : []);
      } catch (e) {
        if (alive) setPayments([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userReduxState.businessType]);

  return (
    <div className="flex flex-col gap-5">
      <SettingsCard title="Your FitClub plan" subtitle="What you pay FitClub for running your club.">
        <div className="mt-5 flex flex-col gap-3">
          {subs === null ? (
            <div className="h-24 animate-pulse rounded-2xl bg-[#F3EEF5]" />
          ) : subs.length === 0 ? (
            <p className="rounded-2xl bg-[#FAF8FB] px-4 py-5 text-sm text-gray-600">No subscription found.</p>
          ) : (
            subs.map((s, i) => {
              const [label, tone] = STATUS[String(s.status || "").toLowerCase()] || [s.status ? String(s.status).charAt(0).toUpperCase() + String(s.status).slice(1) : "–", "bg-[#F1EEF2] text-[#595159]"];
              const pct = subStart(s) && subEnd(s) ? getUsedSubscriptionPercentage(subStart(s), subEnd(s)) : null;
              const start = subStart(s) ? moment.unix(subStart(s)) : null;
              const end = subEnd(s) ? moment.unix(subEnd(s)) : null;
              const days = start && end ? Math.max(1, end.diff(start, "days")) : null;
              const used = start && days ? Math.min(days, Math.max(0, moment().diff(start, "days"))) : null;
              return (
                <div key={s.id || s.number || i} className="flex flex-wrap items-center gap-4 rounded-2xl bg-[#FAF8FB] p-4 sm:p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brown-coffee text-white">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-6 w-6" aria-hidden="true">
                      <path fillRule="evenodd" clipRule="evenodd" d="M2.5 4A1.5 1.5 0 0 0 1 5.5V6h18v-.5A1.5 1.5 0 0 0 17.5 4h-15ZM19 8.5H1v6A1.5 1.5 0 0 0 2.5 16h15a1.5 1.5 0 0 0 1.5-1.5v-6ZM3 13.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Zm4.75-.75a.75.75 0 0 0 0 1.5h3.5a.75.75 0 0 0 0-1.5h-3.5Z" />
                    </svg>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="text-lg font-black text-gray-900">{s?.product_detail?.name || "FitClub plan"}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-extrabold ${tone}`}>{label}</span>
                    </div>
                    <div className="mt-0.5 text-sm text-gray-600">
                      Billed {(INTERVAL[subInterval(s)] || "").toLowerCase() || "–"}
                      {end && !s.cancel_at_period_end ? ` · next invoice ${end.format("D MMM YYYY")} for ${euro(s.upcoming_invoice?.total ?? subAmount(s))}${s.upcoming_invoice?.tax > 0 ? " incl. VAT" : ""}` : ""}
                      {end && s.cancel_at_period_end ? ` · ends ${end.format("D MMM YYYY")}` : ""}
                    </div>
                    {pct !== null && (
                      <div className="mt-3 flex items-center gap-3">
                        <span className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-[#EDE5F0]">
                          <span className="block h-full rounded-full bg-indigo-bz" style={{ width: `${pct}%` }} />
                        </span>
                        {days && <span className="shrink-0 text-xs font-extrabold text-gray-600">{used} of {days} days</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </SettingsCard>

      <SettingsCard title="Invoices" subtitle="Your FitClub invoices, paid through Stripe.">
        <div className="mt-5">
          {invoices === null ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((k) => (
                <div key={k} className="h-11 animate-pulse rounded-xl bg-[#F3EEF5]" />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <p className="rounded-2xl bg-[#FAF8FB] px-4 py-5 text-sm text-gray-600">No invoices yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="w-full min-w-[700px] border-collapse">
                  <thead>
                    <tr className="bg-[#FAF8FB] text-left text-[11px] font-extrabold uppercase tracking-wide text-gray-600">
                      <th className="px-4 py-2.5">Invoice</th>
                      <th className="px-4 py-2.5">Period</th>
                      <th className="px-4 py-2.5">Billing</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5 text-right">Amount</th>
                      <th className="px-4 py-2.5 text-right">
                        <span className="sr-only">Download</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(allInvoices ? invoices : invoices.slice(0, PAGE)).map((inv, i) => {
                      const line = inv?.lines?.data?.[0] || {};
                      const open = inv.status === "open";
                      return (
                        <tr key={inv.number || inv.id || i} className="border-t border-gray-100 text-sm">
                          <td className="px-4 py-3 font-extrabold text-gray-900">{inv.number || "–"}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                            {line.period ? `${moment.unix(line.period.start).format("D MMM YYYY")} – ${moment.unix(line.period.end).format("D MMM YYYY")}` : moment.unix(inv.created).format("D MMM YYYY")}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{INTERVAL[lineInterval(line)] || "One-time"}</td>
                          <td className="px-4 py-3">
                            <Pill tone={open ? "amber" : "green"}>{open ? "Due" : "Paid"}</Pill>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-black text-gray-900">{euro(open ? inv.amount_due : inv.amount_paid)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            {open && inv.hosted_invoice_url ? (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="mr-1 inline-flex items-center rounded-xl bg-brown-coffee px-3 py-1.5 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                                Pay<span className="sr-only">, invoice {inv.number}</span>
                              </a>
                            ) : null}
                            {inv.invoice_pdf ? (
                              <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className={LINK}>
                                {DOWNLOAD}
                                PDF<span className="sr-only">, invoice {inv.number}</span>
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!allInvoices && invoices.length > PAGE && <ShowAll count={invoices.length} what="invoices" onClick={() => setAllInvoices(true)} />}
            </>
          )}
        </div>
      </SettingsCard>

      <SettingsCard title="Payments" subtitle="Every payment you made to FitClub, with its receipt.">
        <div className="mt-5">
          {payments === null ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((k) => (
                <div key={k} className="h-11 animate-pulse rounded-xl bg-[#F3EEF5]" />
              ))}
            </div>
          ) : payments.length === 0 ? (
            <p className="rounded-2xl bg-[#FAF8FB] px-4 py-5 text-sm text-gray-600">No payments yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="w-full min-w-[700px] border-collapse">
                  <thead>
                    <tr className="bg-[#FAF8FB] text-left text-[11px] font-extrabold uppercase tracking-wide text-gray-600">
                      <th className="px-4 py-2.5">Date</th>
                      <th className="px-4 py-2.5">For</th>
                      <th className="px-4 py-2.5">Paid via</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5 text-right">Amount</th>
                      <th className="px-4 py-2.5 text-right">
                        <span className="sr-only">Receipt</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(allPayments ? payments : payments.slice(0, PAGE)).map((p) => {
                      const st = PAYMENT_STATUS[p.status] || PAYMENT_STATUS.pending;
                      return (
                        <tr key={p.id} className="border-t border-gray-100 align-top text-sm">
                          <td className="whitespace-nowrap px-4 py-3 text-gray-600">{moment.unix(p.created).format("D MMM YYYY")}</td>
                          <td className="px-4 py-3">
                            <span className="block font-extrabold text-gray-900">{p.description}</span>
                            {p.invoiceNumber && <span className="block whitespace-nowrap text-xs text-gray-500">Invoice {p.invoiceNumber}</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{p.method || "–"}</td>
                          <td className="px-4 py-3">
                            <Pill tone={st.tone}>{p.status === "partially_refunded" ? `Refunded ${euro(p.amountRefunded)}` : st.label}</Pill>
                            {p.status === "failed" && p.failureMessage && <span className="mt-1 block max-w-[220px] text-xs text-[#B4232F]">{p.failureMessage}</span>}
                          </td>
                          <td className={`whitespace-nowrap px-4 py-3 text-right font-black ${p.status === "failed" ? "text-gray-400 line-through decoration-1" : "text-gray-900"}`}>{euro(p.amount)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            {p.receiptUrl ? (
                              <a href={p.receiptUrl} target="_blank" rel="noreferrer" className={LINK}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                                  <path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" />
                                  <path d="M9 7h6M9 11h6" />
                                </svg>
                                Receipt<span className="sr-only">, payment of {moment.unix(p.created).format("D MMM YYYY")}</span>
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!allPayments && payments.length > PAGE && <ShowAll count={payments.length} what="payments" onClick={() => setAllPayments(true)} />}
            </>
          )}
        </div>
      </SettingsCard>
    </div>
  );
}
